import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Input, Button, Space, Modal, message, Typography, Tag, Row, Col, Upload, Drawer } from 'antd';
import {
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  SaveOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  SyncOutlined

} from '@ant-design/icons';
import axios from 'axios';
import { fetchComponents as fetchComponentsApi, addComponent, updateComponent, deleteComponent as deleteComponentApi, moveComponentUp as moveComponentUpApi, moveComponentDown as moveComponentDownApi } from '../services/componentsService';
const { Text, Title } = Typography;

const PackagesTable = () => {
  const { state } = useLocation();
  const packageId = state.packageId;
  const [components, setComponents] = useState([]);
  const [createComponentModalVisible, setCreateComponentModalVisible] = useState(false);
  const [newComponentName, setNewComponentName] = useState('');
  const [packageDetails, setPackageDetails] = useState();
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [materialsModalVisible, setMaterialsModalVisible] = useState(false); const [notes, setNotes] = useState([]);
  const [materials, setMaterials] = useState([]);

  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  useEffect(() => {
    if (packageId) {
      fetchPackageDetails();
      fetchComponents();
      fetchNotes();
      fetchMaterials();
    }
  }, [packageId]);

  // Notes API functions
  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${baseUrl}/package-notes/${packageId}`);
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Failed to fetch notes');
    }
  };

  const handleAddNote = async () => {
    try {
      const newNoteData = {
        packageId: packageId,
        content: ''
      };
      await axios.post(`${baseUrl}/package-notes`, newNoteData);
      await fetchNotes();
      message.success('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      message.error('Failed to add note');
    }
  };

  const handleSaveNote = async (noteId, content) => {
    try {
      await axios.put(`${baseUrl}/package-notes/${noteId}`, { content });
      await fetchNotes();
      message.success('Note updated successfully');
    } catch (error) {
      console.error('Error updating note:', error);
      message.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${baseUrl}/package-notes/${noteId}`);
      await fetchNotes();
      message.success('Note deleted successfully');
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Failed to delete note');
    }
  };

  // Materials API functions
  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${baseUrl}/package-materials/${packageId}`);
      setMaterials(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching materials:', error);
      message.error('Failed to fetch materials');
    }
  };

  const handleAddMaterial = async () => {
    try {
      const newMaterialData = {
        packageId: packageId,
        name: ''
      };
      await axios.post(`${baseUrl}/package-materials`, newMaterialData);
      await fetchMaterials();
      message.success('Material added successfully');
    } catch (error) {
      console.error('Error adding material:', error);
      message.error('Failed to add material');
    }
  };

  const handleSaveMaterial = async (materialId, name) => {
    try {
      await axios.put(`${baseUrl}/package-materials/${materialId}`, { name });
      await fetchMaterials();
      message.success('Material updated successfully');
    } catch (error) {
      console.error('Error updating material:', error);
      message.error('Failed to update material');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await axios.delete(`${baseUrl}/package-materials/${materialId}`);
      await fetchMaterials();
      message.success('Material deleted successfully');
    } catch (error) {
      console.error('Error deleting material:', error);
      message.error('Failed to delete material');
    }
  };

  const fetchComponents = async () => {
    try {
      const componentsData = await fetchComponentsApi(packageId);
      setComponents(componentsData);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch components');
    }
  };

  const fetchPackageDetails = async () => {
    try {
      const res = await axios.get(`${baseUrl}/packages/${packageId}`);
      if (res.data) {
        setPackageDetails(res.data);
        document.title = `Package: ${res.data.PackageName}`;
      } else {
        document.title = 'Package Details';
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch package details');
    }
  };

  const updateComponentName = async (compId, newName) => {
    try {
      await updateComponent(compId, { ComponentType: newName });
      message.success('Component updated');
      fetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to update component');
    }
  };

  const confirmDeleteComponent = (compId, compName) => {
    Modal.confirm({
      title: 'Delete Component',
      content: `Are you sure you want to delete component "${compName}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => deleteComponent(compId),
    });
  };

  const deleteComponent = async (compId) => {
    try {
      await deleteComponentApi(compId);
      message.success('Component deleted');
      fetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to delete component');
    }
  };

  const moveComponentUp = async (compId) => {
    try {
      await moveComponentUpApi(compId);
      message.success('Component moved up');
      fetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to move component up');
    }
  };

  const moveComponentDown = async (compId) => {
    try {
      await moveComponentDownApi(compId);
      message.success('Component moved down');
      fetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to move component down');
    }
  };
  const handleCreateComponent = async () => {
    try {
      await addComponent({
        PackageID: packageId,
        ComponentType: newComponentName,
        DisplayOrder: components.length + 1,
      });
      message.success('Component created');
      setCreateComponentModalVisible(false);
      setNewComponentName('');
      fetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to create component');
    }
  }; const handleEditNote = (id) => {
    setNotes(notes.map(note =>
      note.id === id ? { ...note, isEditing: true } : note
    ));
  };

  const handleEditMaterial = (id) => {
    setMaterials(materials.map(material =>
      material.id === id ? { ...material, isEditing: true } : material
    ));
  };

  return (
    <div
      style={{ boxShadow: 'none', borderRadius: 8, padding: '16px 24px' }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Package : - {packageDetails ? packageDetails.PackageName : 'Package Components'}</Title>
          <Text type="secondary">Manage your tentative quotation data</Text>
        </Col>        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateComponentModalVisible(true)}
            >
              Add Space
            </Button>
            <Button
              onClick={() => setNotesModalVisible(true)}
            >
              Manage Notes
            </Button>
            <Button
              onClick={() => setMaterialsModalVisible(true)}
            >
              Manage Materials
            </Button>
          </Space>
        </Col>
      </Row>      {/* Notes Drawer */}
      <Drawer
        title="Manage Notes"
        placement="right"
        onClose={() => setNotesModalVisible(false)}
        open={notesModalVisible}
        width={800}
        extra={
          <Button type="primary" onClick={handleAddNote} icon={<PlusOutlined />}>
            Add Item
          </Button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0', width: '60px' }}>Sr. No</th>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0' }}>Note</th>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((note, index) => (
              <tr key={note.id}>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0', textAlign: 'center' }}>{index + 1}</td>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0' }}>
                  {note.isEditing ? (
                    <Input.TextArea
                      defaultValue={note.content}
                      autoSize
                      onBlur={(e) => handleSaveNote(note.id, e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <div style={{ minHeight: '22px' }}>{note.content}</div>
                  )}
                </td>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setNotes(notes.map(n =>
                        n.id === note.id ? { ...n, isEditing: true } : n
                      ))}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteNote(note.id)}
                    />
                  </Space>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Drawer>

      {/* Materials Drawer */}
      <Drawer
        title="Manage Materials"
        placement="right"
        onClose={() => setMaterialsModalVisible(false)}
        open={materialsModalVisible}
        width={800}
        extra={
          <Button type="primary" onClick={handleAddMaterial} icon={<PlusOutlined />}>
            Add Item
          </Button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0', width: '60px' }}>Sr. No</th>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0' }}>Material</th>
              <th style={{ padding: '12px', border: '1px solid #f0f0f0', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((material, index) => (
              <tr key={material.id}>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0', textAlign: 'center' }}>{index + 1}</td>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0' }}>
                  {material.isEditing ? (
                    <Input
                      defaultValue={material.name}
                      onBlur={(e) => handleSaveMaterial(material.id, e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <div>{material.name}</div>
                  )}
                </td>
                <td style={{ padding: '12px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setMaterials(materials.map(m =>
                        m.id === material.id ? { ...m, isEditing: true } : m
                      ))}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteMaterial(material.id)}
                    />
                  </Space>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Drawer>


      {components.map(comp => (
        <ComponentCard
          key={comp.ComponentID}
          component={comp}
          updateComponentName={updateComponentName}
          deleteComponent={confirmDeleteComponent}
          moveUp={moveComponentUp}
          moveDown={moveComponentDown}
          onWorkItemsChange={fetchComponents}
        />
      ))}

      <Modal
        title="Add Component"
        visible={createComponentModalVisible}
        onOk={handleCreateComponent}
        onCancel={() => setCreateComponentModalVisible(false)}
        okText="Create"
      >
        <Input
          value={newComponentName}
          onChange={(e) => setNewComponentName(e.target.value)}
          placeholder="Component Name"
        />
      </Modal>
    </div>
  );
};

const ComponentCard = ({ component, updateComponentName, deleteComponent, moveUp, moveDown, onWorkItemsChange, onTotalChange }) => {
  const [editingName, setEditingName] = useState(false);
  const [componentName, setComponentName] = useState(component.ComponentType);
  const [workItemsData, setWorkItemsData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingWorkItemId, setEditingWorkItemId] = useState(null);

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const widthRefs = useRef([]);

  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  useEffect(() => {
    fetchWorkItems();
  }, [component.ComponentID]);

  useEffect(() => {
    const total = workItemsData.reduce((sum, item) => {
      const manualSqFt = Number(item.SqFt || 0);
      const nos = Number(item.Nos || 0);
      const width = Number(item.Width || 0);
      const length = Number(item.Length || 0);
      const price = Number(item.PricePerFeet || 0);
      let sqft = 0;
      if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
        sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * (nos || 1);
      } else if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
        sqft = manualSqFt;
      } else {
        sqft = length * width * (nos || 1);
      }
      let total = 0;
      if (sqft === 0) {
        total = nos * price;
      } else {
        total = sqft * price;
      }
      return sum + (isNaN(total) ? 0 : total);
    }, 0);
    if (onTotalChange) onTotalChange(component.ComponentID, total);
  }, [workItemsData, component.ComponentID, onTotalChange]);

  const saveModifiedItems = async () => {
    const modifiedItems = workItemsData.filter(item => item.isModified);
    if (modifiedItems.length === 0) return;
    setIsSaving(true);
    try {
      for (const item of modifiedItems) {
        try {
          const { isModified, ...itemData } = item;
          let SqFt = Number(item.SqFt || 0);
          const nos = Number(item.Nos || 0);
          const width = Number(item.Width || 0);
          const length = Number(item.Length || 0);
          if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
            SqFt = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * (nos || 1);
          } else if ((length !== 0 && width !== 0)) {
            SqFt = length * width * (nos || 1);
          }
          await axios.put(`${baseUrl}/workitems/${item.WorkItemID}`, { ...itemData, SqFt });
          setWorkItemsData(prev =>
            prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, isModified: false } : wi)
          );
        } catch (error) {
          console.error('Failed to autosave item:', item, error);
        }
      }
      onWorkItemsChange(); // Refresh components and grand total
    } finally {
      setIsSaving(false);
    }
  };

  const fetchWorkItems = async () => {
    try {
      const res = await axios.get(`${baseUrl}/workitems?componentId=${component.ComponentID}`);
      setWorkItemsData(res.data);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch work items');
    }
  };

  const handleWorkItemChange = (id, field, value) => {
    setWorkItemsData(prev =>
      prev.map(item => (item.WorkItemID === id ? { ...item, [field]: value, isModified: true } : item))
    );
  };

  const handleBlur = async (item) => {
    if (item.isModified) {
      await saveWorkItem(item);
    }
  };

  const saveWorkItem = async (item) => {
    try {
      setIsSaving(true);
      const { isModified, ...itemData } = item;
      // Calculate SqFt for payload
      let SqFt = Number(item.SqFt || 0);
      const nos = Number(item.Nos || 0);
      const width = Number(item.Width || 0);
      const length = Number(item.Length || 0);
      if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
        SqFt = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * (nos || 1);
      } else if ((length !== 0 && width !== 0)) {
        SqFt = length * width * (nos || 1);
      }
      await axios.put(`${baseUrl}/workitems/${item.WorkItemID}`, { ...itemData, SqFt });
      setWorkItemsData(prev =>
        prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...itemData, isModified: false } : wi)
      );
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to save work item: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmRemoveWorkItem = (id) => {
    Modal.confirm({
      title: 'Delete Work Item',
      content: 'Are you sure you want to delete this work item?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => removeWorkItem(id),
    });
  };

  const removeWorkItem = async (id) => {
    try {
      await axios.delete(`${baseUrl}/workitems/${id}`);
      setWorkItemsData(prev => prev.filter(item => item.WorkItemID !== id));
      message.success('Work item removed');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to remove work item');
    }
  };

  const moveWorkItemUp = async (item) => {
    try {
      await axios.post(`${baseUrl}/workitems/${item.WorkItemID}/move-up`);
      const res = await axios.get(`${baseUrl}/workitems?componentId=${component.ComponentID}`);
      if (Array.isArray(res.data)) {
        setWorkItemsData(res.data);
        message.success('Work item moved up');
        onWorkItemsChange(); // Refresh components and grand total
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to move work item up');
    }
  };

  const moveWorkItemDown = async (item) => {
    try {
      await axios.post(`${baseUrl}/workitems/${item.WorkItemID}/move-down`);
      const res = await axios.get(`${baseUrl}/workitems?componentId=${component.ComponentID}`);
      if (Array.isArray(res.data)) {
        setWorkItemsData(res.data);
        message.success('Work item moved down');
        onWorkItemsChange(); // Refresh components and grand total
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to move work item down');
    }
  };

  const addWorkItem = async () => {
    try {
      const nos = Number(workItemsData.length + 1);
      const newItem = {
        ComponentID: component.ComponentID,
        SrNo: nos,
        CarpentryWork: '-',
        Nos: '0',
        Height: '0',
        Length: '0',
        Width: '0',
        PricePerFeet: '0',
        SqFt: 0
      };
      await axios.post(`${baseUrl}/workitems`, newItem);
      fetchWorkItems();
      message.success('New work item added');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to add work item: ' + (error.response?.data?.error || error.message));
    }
  };

  // CSV download function
  const handleDownloadCSV = () => {
    try {
      // Create CSV headers
      const headers = ['Sr No', 'Item', 'Nos', 'Width', 'Length', 'Sq. Ft.', 'Rs./sFt.', 'Total'];

      // Convert work items to CSV rows
      const csvRows = workItemsData.map((item, index) => {
        const nos = Number(item.Nos || 0) || 1;
        const width = Number(item.Width || 0);
        const length = Number(item.Length || 0);
        const price = Number(item.PricePerFeet || 0);

        // Calculate Sq.Ft.
        let sqft = 0;
        if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
          sqft = Number(item.SqFt || 0);
        } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
          sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
        } else {
          sqft = length * width * nos;
        }

        // Calculate total
        const total = sqft === 0 ? (nos * price) : (sqft * price);

        return [
          index + 1,
          item.CarpentryWork,
          item.Nos,
          item.Width,
          item.Length,
          sqft.toFixed(2),
          item.PricePerFeet,
          total.toFixed(2)
        ];
      });

      // Combine headers and rows
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${component.ComponentType.replace(/[^a-z0-9]/gi, '_')}_items.csv`;
      link.click();

      message.success('CSV downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      message.error('Failed to download CSV');
    }
  };

  // CSV upload function
  const handleUploadCSV = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const rows = text.split('\n').map(row =>
          row.split(',').map(cell => cell.replace(/^"(.*)"$/, '$1').trim())
        );

        // Skip header row
        const dataRows = rows.slice(1);

        // Process each row
        for (const row of dataRows) {
          if (row.length >= 7) { // Ensure row has minimum required columns
            const newItem = {
              ComponentID: component.ComponentID,
              CarpentryWork: row[1],
              Nos: row[2],
              Width: row[3],
              Length: row[4],
              PricePerFeet: row[6],
              SqFt: row[5]
            };

            try {
              await axios.post(`${baseUrl}/workitems`, newItem);
            } catch (err) {
              console.error('Error adding work item:', err);
            }
          }
        }

        await fetchWorkItems();
        message.success('CSV data imported successfully');
        onWorkItemsChange();
      };

      reader.readAsText(file);
      return false; // Prevent automatic Upload component upload
    } catch (error) {
      console.error('Error uploading CSV:', error);
      message.error('Failed to upload CSV');
      return false;
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {editingName ? (
            <>
              <Input
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                style={{ marginRight: 8, width: 200 }}
              />
              <Button
                type="primary"
                size="medium"
                onClick={() => {
                  updateComponentName(component.ComponentID, componentName);
                  setEditingName(false);
                }}
              >
                Save
              </Button>
              <Button
                size="medium"
                onClick={() => {
                  setEditingName(false);
                  setComponentName(component.ComponentType);
                }}
                style={{ marginLeft: 4 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '15px', fontWeight: 600 }}>{component.ComponentType}</span>
              <Button type="text" icon={<EditOutlined />} size="medium" onClick={() => setEditingName(true)} style={{ marginLeft: 8 }} />
              <Button type="text" icon={<ArrowUpOutlined />} size="medium" onClick={() => moveUp(component.ComponentID)} style={{ marginLeft: 8 }} />
              <Button type="text" icon={<ArrowDownOutlined />} size="medium" onClick={() => moveDown(component.ComponentID)} style={{ marginLeft: 8 }} />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="medium"
                onClick={() => deleteComponent(component.ComponentID, component.ComponentType)}
                style={{ marginLeft: 4 }}
              />
              {isSaving && <Tag color="blue" style={{ marginLeft: 8, borderRadius: '4px' }}>Saving...</Tag>}

            </>
          )}
        </div>} extra={
          <Space size="medium">
            {isLocalhost && (
              <>
                <Button
                  type="default"
                  size="medium"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadCSV}
                >
                  Export CSV
                </Button>
                <Upload
                  beforeUpload={handleUploadCSV}
                  showUploadList={false}
                  accept=".csv"
                >
                  <Button
                    type="default"
                    size="medium"
                    icon={<UploadOutlined />}
                  >
                    Import CSV
                  </Button>
                </Upload>
              </>
            )}
            <Button
              type="primary"
              size="medium"
              icon={<PlusOutlined />}
              onClick={addWorkItem}
            >
              Add Work Item
            </Button>
            <Button
              type="default"
              size="medium"
              icon={<SyncOutlined />}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  // Prepare array of { WorkItemID, SrNo } in current order
                  const srNoList = workItemsData.map((item, idx) => ({
                    WorkItemID: item.WorkItemID,
                    SrNo: idx + 1
                  }));
                  await axios.post(`${baseUrl}/workitems/sync-all-srno`, {
                    componentId: component.ComponentID,
                    srNoList
                  });
                  message.success('SrNo order synced with backend');
                  fetchWorkItems();
                  onWorkItemsChange();
                } catch (error) {
                  console.error(error);
                  message.error('Failed to sync SrNo order');
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Sync Order
            </Button>
          </Space>
        }
      style={{
        marginBottom: 16,
        borderRadius: '8px',
        overflow: 'hidden'
      }}
      bodyStyle={{ padding: 0 }}
      headStyle={{
        padding: '12px 16px',
      }}
    >
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #e0e0e0'
      }}>
        <thead>
          <tr>
            {['Sr No', 'Item', 'Nos', 'Width', 'Length', 'Sq. Ft.', 'Rs./sFt.', 'Total', 'Actions'].map((header, index) => (
              header === 'Height' ? null : (
                <th key={header} style={{
                  padding: '4px',
                  fontWeight: 500,
                  fontSize: '13px',
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                  height: '32px',
                  width: index === 1 ? 'auto' : (index === 0 ? '50px' : '80px')
                }}>
                  {header}
                </th>
              )
            ))}
          </tr>
        </thead>
        <tbody>
          {workItemsData.map((item, index) => (
            <tr key={item.WorkItemID} style={{ backgroundColor: item.isModified ? '#FEF9C3' : '#fff' }}>
              <td style={{
                padding: '0',
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}>
                <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                  {index + 1}
                </Typography.Text>
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input.TextArea
                  value={item.CarpentryWork}
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'CarpentryWork', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    '&:focus': {
                      boxShadow: 'none',
                      border: '2px solid #1890ff'
                    }
                  }}
                  autoSize
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={Number(item['Nos']).toFixed(0)}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Nos', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  ref={el => widthRefs.current[index] = el}
                  value={item['Width']}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Width', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const next = workItemsData[index + 1];
                      if (next) {
                        setEditingWorkItemId(next.WorkItemID);
                        setTimeout(() => {
                          if (widthRefs.current[index + 1]) {
                            widthRefs.current[index + 1].focus();
                          }
                        }, 0);
                      }
                    }
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={item['Length']}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Length', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              {/* Sq. Ft. */}
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                {((Number(item.Length || 0) === 0) && (Number(item.Width || 0) === 0)) ? (
                  <Input
                    value={item.SqFt || ''}
                    type="number"
                    onChange={e => handleWorkItemChange(item.WorkItemID, 'SqFt', e.target.value)}
                    onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                    onBlur={() => {
                      handleBlur(item);
                      setEditingWorkItemId(null);
                    }}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                    style={{
                      fontSize: '13px',
                      padding: '4px',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      textAlign: 'right',
                    }}
                  />
                ) : (
                  <Input
                    value={(() => {
                      const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                      const width = Number(item.Width || 0);
                      const length = Number(item.Length || 0);
                      if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                        return ((length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos).toFixed(2);
                      }
                      return (length * width * nos).toFixed(2);
                    })()}
                    disabled
                    style={{
                      fontSize: '13px',
                      padding: '4px',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      textAlign: 'right',
                    }}
                  />
                )}
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={item.PricePerFeet}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'PricePerFeet', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={(() => {
                    const manualSqFt = Number(item.SqFt || 0);
                    const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                    const width = Number(item.Width || 0);
                    const length = Number(item.Length || 0);
                    const price = Number(item.PricePerFeet || 0);
                    let sqft = 0;
                    if ((!item.Length && !item.Width) || (Number(item.Length) === 0 && Number(item.Width) === 0)) {
                      sqft = manualSqFt;
                    } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                      // If either length or width is 0, treat the other as 1
                      sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
                    } else {
                      sqft = length * width * nos;
                    }
                    const total = sqft === 0 ? (nos * price) : (sqft * price);
                    return total.toFixed(2);
                  })()}
                  disabled
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right',
                  }}
                />
              </td>
              <td style={{
                padding: '0',
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}>
                <Space size="medium">
                  <Button
                    type="text"
                    icon={<ArrowUpOutlined />}
                    size="medium"
                    onClick={() => moveWorkItemUp(item)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  <Button
                    type="text"
                    icon={<ArrowDownOutlined />}
                    size="medium"
                    onClick={() => moveWorkItemDown(item)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="medium"
                    onClick={() => confirmRemoveWorkItem(item.WorkItemID)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  {item.isModified && (
                    <Button
                      type="text"
                      icon={<SaveOutlined />}
                      size="medium"
                      onClick={() => saveWorkItem(item)}
                      style={{ color: '#059669' }}
                      disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                    />
                  )}
                </Space>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7} style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, background: '#f5f5f5', border: '1px solid #e0e0e0' }}>Component Total</td>
            <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, background: '#f5f5f5', border: '1px solid #e0e0e0' }}>
              {(() => {
                return workItemsData.reduce((sum, item) => {
                  const manualSqFt = Number(item.SqFt || 0);
                  const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                  const width = Number(item.Width || 0);
                  const length = Number(item.Length || 0);
                  const price = Number(item.PricePerFeet || 0);
                  let sqft = 0;
                  if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
                    sqft = manualSqFt;
                  } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                    // If either length or width is 0, treat the other as 1
                    sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
                  } else {
                    sqft = length * width * nos;
                  }
                  const total = sqft === 0 ? (nos * price) : (sqft * price);
                  return sum + (isNaN(total) ? 0 : total);
                }, 0).toFixed(2);
              })()}
            </td>
            <td style={{ border: '1px solid #e0e0e0' }}></td>
          </tr>
        </tfoot>
      </table>
    </Card>
  );
};

export default PackagesTable;